<!--
	08 / PRICING (plan/04 §06) — the single founder card, shared verbatim between
	`/` and `/pricing`. Prerendered HTML never shows a seat count (no number at
	all until hydration resolves it — see the $effect below); the buy button is a
	plain `<form method="POST" action="/api/checkout">` so checkout works with no
	JS. Also owns three edge states from plan/04 §5:
	  - sold out (live count hits 0, or the checkout redirect says so) → the CTA
	    flips to an EmailCapture, reusing the card's own waitlist prompt copy.
	  - `#pricing?checkout=cancelled` / `#pricing?soldout=1` — checkout cancellation
	    and the sold-out checkout guard both redirect into the URL *fragment*
	    (`/#pricing?...`), not real query params, since they come after the `#`.
	    That also means the browser can't natively scroll to id="pricing" (the
	    whole "pricing?checkout=cancelled" string is the fragment) — so this
	    component parses the fragment itself and scrolls itself into view.
	  - `?subscribed=1|0` — the no-JS path of the waitlist form below (and in
	    sold-out mode) redirects back with a real query param.

	The card is the one thing on the page allowed to sit on paper in full white with
	the accent fill in it — everything else in view is quiet so that this reads as
	the destination the whole scroll has been walking toward.
-->
<script lang="ts">
	import { page } from '$app/state';
	import { Button, Readout, MonoLabel, EmailCapture, Section } from '$lib/components/ui';
	import { pricingCard, pricing } from '$lib/content/pricing';
	import { whoWeSellTo } from '$lib/content/scenarios';
	import { microcopy } from '$lib/content/site';
	import { SprungNumber } from '$lib/motion/sprung.svelte';
	import { trackEvent } from '$lib/analytics';
	import { demo } from '$lib/content/demo';

	/** true on /pricing (its own page → real <h1>); false embedded on the homepage (→ h2). */
	let { standalone = false }: { standalone?: boolean } = $props();

	type SeatState =
		| { status: 'loading' }
		| { status: 'ok'; remaining: number }
		| { status: 'soldout' }
		| { status: 'unknown' };

	let seats = $state<SeatState>({ status: 'loading' });

	// Client-only hydration of the live presale count (plan/04 §06). `remaining`
	// is the server's number (paid orders plus config.offsite_orders, against the
	// cap) and the same number gates checkout, so what renders here is exactly
	// what can still be bought. While pending/failed, no number renders — the
	// prerendered/no-JS state (bullets + buy form, no readout) stays the fallback.
	$effect(() => {
		let cancelled = false;
		fetch('/api/seats')
			.then((res) => (res.ok ? res.json() : Promise.reject(res.status)))
			.then((data: { remaining: number | null }) => {
				if (cancelled) return;
				if (typeof data.remaining !== 'number') seats = { status: 'unknown' };
				else if (data.remaining <= 0) seats = { status: 'soldout' };
				else seats = { status: 'ok', remaining: data.remaining };
			})
			.catch(() => {
				if (!cancelled) seats = { status: 'unknown' };
			});
		return () => {
			cancelled = true;
		};
	});

	/** `#pricing?checkout=cancelled` puts its payload in the URL fragment, not the
	 * query string — everything after `#` is one opaque fragment, so it needs its
	 * own parse rather than `page.url.searchParams`. */
	function parseHashParams(hash: string): URLSearchParams {
		const i = hash.indexOf('?');
		return new URLSearchParams(i === -1 ? '' : hash.slice(i + 1));
	}

	// `page.url.searchParams` (and, defensively, `.hash`) can only be read
	// client-side: SvelteKit throws if a prerendered page's render pass touches
	// them, since they'd just reflect whatever the build-time URL happened to be.
	// Reading them inside an $effect keeps that out of the SSR/prerender pass —
	// these three start at their "nothing going on" defaults and only pick up a
	// real value once this runs in the browser (and again on client-side nav).
	let checkoutCancelled = $state(false);
	let forcedSoldOut = $state(false);
	let subscribedParam = $state<string | null>(null);

	$effect(() => {
		const hashParams = parseHashParams(page.url.hash);
		checkoutCancelled = hashParams.get('checkout') === 'cancelled';
		forcedSoldOut = hashParams.get('soldout') === '1';
		subscribedParam = page.url.searchParams.get('subscribed');
	});

	const isSoldOut = $derived(forcedSoldOut || seats.status === 'soldout');

	// Claimed = cap minus the server's remaining. No display-side arithmetic: the
	// licenses sold outside the site live in config.offsite_orders and are
	// already inside `remaining` (see $lib/server/pocketbase).
	const sold = $derived(seats.status === 'ok' ? pricing.founderCap - seats.remaining : 0);
	const displayRemaining = $derived(seats.status === 'ok' ? seats.remaining : 0);

	// The scarcity bar fills (and the claimed count ticks up) only once the aside
	// is actually in view — an animation that played off-screen while the fetch
	// resolved would be wasted. Until then the bar sits at 0; the count itself is
	// always the real PocketBase figure, the spring is only the flourish.
	const claimed = new SprungNumber(0);
	const claimedPct = $derived(Math.round((claimed.current / pricing.founderCap) * 1000) / 10);

	let asideEl: HTMLElement | undefined = $state();
	let asideSeen = $state(false);

	$effect(() => {
		if (!asideEl) return;
		const io = new IntersectionObserver(
			([entry]) => {
				if (entry.isIntersecting) {
					asideSeen = true;
					io.disconnect();
				}
			},
			{ threshold: 0.35 }
		);
		io.observe(asideEl);
		return () => io.disconnect();
	});

	$effect(() => {
		if (asideSeen && seats.status === 'ok') claimed.set(sold);
		if (isSoldOut) claimed.set(pricing.founderCap);
	});

	type NoticeKind = 'cancelled' | 'subscribed-yes' | 'subscribed-no' | null;
	const noticeKind = $derived<NoticeKind>(
		checkoutCancelled
			? 'cancelled'
			: subscribedParam === '1'
				? 'subscribed-yes'
				: subscribedParam === '0'
					? 'subscribed-no'
					: null
	);
	const noticeText = $derived(
		noticeKind === 'cancelled'
			? microcopy.checkoutCancelled
			: noticeKind === 'subscribed-yes'
				? microcopy.waitlist.success
				: noticeKind === 'subscribed-no'
					? microcopy.validation.emailIncomplete
					: ''
	);

	let noticeDismissed = $state(false);

	// Auto-dismiss (plan/04 §5) plus a manual close button below — never rely on
	// the timer alone for something a screen-reader/keyboard user needs to catch.
	$effect(() => {
		if (!noticeKind) return;
		noticeDismissed = false;
		const timer = setTimeout(() => {
			noticeDismissed = true;
		}, 6000);
		return () => clearTimeout(timer);
	});

	const showNotice = $derived(noticeKind !== null && !noticeDismissed);

	// The redirect landed on `#pricing?...`, which the browser cannot resolve to
	// id="pricing" on its own (see the file header) — do it ourselves.
	$effect(() => {
		if (checkoutCancelled || forcedSoldOut) {
			document.getElementById('pricing')?.scrollIntoView({ block: 'start' });
		}
	});

	let submitting = $state(false);
</script>

<Section id="pricing" tone="panel">
	<div class="head">
		<p class="head__eyebrow">
			<span class="head__tick" aria-hidden="true"></span>
			<span class="head__index">09</span>
			<span>Pricing</span>
		</p>
		{#if standalone}
			<h1 class="head__h">{pricingCard.eyebrow}</h1>
		{:else}
			<h2 class="head__h">{pricingCard.eyebrow}</h2>
		{/if}
		<p class="head__sub">{pricingCard.saveLine}</p>
	</div>

	<div class="layout">
		<!-- The card -->
		<div class="card">
			<div class="card__bar">
				<span class="card__marks" aria-hidden="true"></span>
				<span class="card__cap">Presale license</span>
			</div>

			<div class="card__body">
				<!-- The scarcity ladder: what you pay now, and what it reverts to. -->
				<div class="ladder">
					<div class="ladder__markers" aria-hidden="true">
						<span class="ladder__marker" class:is-active={!isSoldOut}></span>
						<span class="ladder__marker" class:is-active={isSoldOut}></span>
					</div>

					<div class="ladder__tiers">
						<div>
							<p class="ladder__price" class:ladder__price--spent={isSoldOut}>
								{pricingCard.price}
							</p>
							<p class="ladder__suffix">{pricingCard.priceSuffix}</p>
							<p class="ladder__label" class:ladder__label--accent={!isSoldOut}>
								{#if isSoldOut}
									Sold out
								{:else if seats.status === 'ok'}
									<span class="ladder__count">{displayRemaining}</span> spots left
								{:else}
									Presale
								{/if}
							</p>
						</div>

						<div class="ladder__next">
							<p class="ladder__next-price">
								{pricingCard.nextPrice}<span class="ladder__next-suffix"
									>{pricingCard.nextPriceSuffix}</span
								>
							</p>
							<p class="ladder__label">{pricingCard.nextPriceLabel}</p>
						</div>
					</div>
				</div>

				<ul class="bullets">
					{#each pricingCard.bullets as bullet (bullet)}
						<li>{bullet}</li>
					{/each}
				</ul>

				<p class="card__who">
					<MonoLabel>{whoWeSellTo.short}</MonoLabel>
				</p>

				{#if isSoldOut}
					<p class="card__prompt">{pricingCard.waitlistPrompt}</p>
					<EmailCapture source="soldout" class="card__capture" />
				{:else}
					<form
						method="POST"
						action="/api/checkout"
						class="card__buy"
						onsubmit={() => {
							submitting = true;
							trackEvent(
								'Checkout Started',
								{
									props: {
										placement: standalone ? 'pricing-page' : 'homepage-pricing',
										product: 'founder-presale'
									},
									ecommerce: {
										currency: pricing.currency,
										value: pricing.founderYear,
										items: [
											{
												item_id: 'founder-presale',
												item_name: 'PPF Engine founder presale',
												price: pricing.founderYear,
												quantity: 1
											}
										]
									}
								},
								'begin_checkout'
							);
						}}
					>
						<input
							type="hidden"
							name="placement"
							value={standalone ? 'pricing-page' : 'homepage-pricing'}
						/>
						<Button type="submit" size="lg" loading={submitting}>
							{submitting ? microcopy.buttons.processing : pricingCard.cta}
						</Button>
					</form>
				{/if}

				{#if showNotice}
					<p role="status" class="notice">
						<span>{noticeText}</span>
						<button
							type="button"
							class="notice__dismiss"
							onclick={() => (noticeDismissed = true)}
							aria-label="Dismiss"
						>
							×
						</button>
					</p>
				{/if}
			</div>

			{#if !isSoldOut}
				<div class="card__terms">
					<p>{pricingCard.refundLine}</p>
					<p>{pricingCard.deliveryLine}</p>
					<p>{pricingCard.taxLine}</p>
				</div>
			{/if}

			<p class="card__footnote">
				<MonoLabel>{pricingCard.footnote}</MonoLabel>
			</p>
		</div>

		<!-- Aside: the live cap, then the waitlist -->
		<div class="aside" bind:this={asideEl}>
			<Readout label="Presale licenses" value={`${pricing.founderCap} (once, ever)`} size="md" />

			{#if isSoldOut}
				<div class="prog">
					<div class="prog__track"><div class="prog__fill" style="width: 100%"></div></div>
					<p class="prog__label">{pricing.founderCap} claimed · sold out</p>
				</div>
			{:else if seats.status === 'ok'}
				<div class="prog">
					<div class="prog__track">
						<div class="prog__fill" style="width: {claimedPct}%"></div>
					</div>
					<p class="prog__label">
						<span class="prog__live" aria-hidden="true"></span><span class="prog__n"
							>{Math.round(claimed.current)}</span
						>
						claimed · {displayRemaining} left
					</p>
				</div>
			{:else}
				<p class="prog__label prog__label--wait">Live availability loading</p>
			{/if}

			{#if !isSoldOut}
				<div class="aside__waitlist">
					<p class="aside__prompt">
						Want to see it run first? <a href="/ppf-pattern-software#demo">{demo.cta}</a> on your own
						missing-pattern job. No payment needed.
					</p>
				</div>
				<div class="aside__waitlist">
					<p class="aside__prompt">{pricingCard.waitlistPrompt}</p>
					<EmailCapture source="pricing" class="aside__capture" />
				</div>
			{/if}
		</div>
	</div>
</Section>

<style>
	/* ── Section head (local: the h1/h2 swap is this component's own) ────── */
	.head__eyebrow {
		display: flex;
		align-items: center;
		gap: 0.625rem;
		font-family: var(--font-mono);
		font-size: var(--text-mono-s);
		letter-spacing: 0.08em;
		text-transform: uppercase;
		color: var(--color-ink-400);
		margin-bottom: 1.25rem;
	}
	.head__tick {
		width: 6px;
		height: 6px;
		flex: none;
		background-color: var(--color-cut-600);
	}
	.head__index {
		/* ink-400: the index is text (WCAG AA), not decoration. */
		color: var(--color-ink-400);
	}
	.head__h {
		font-size: var(--text-h2);
		font-weight: 600;
		line-height: 1.04;
		letter-spacing: -0.025em;
	}
	.head__sub {
		margin-top: 1.25rem;
		max-width: var(--container-measure);
		font-size: var(--text-lead);
		color: var(--color-ink-500);
	}

	.layout {
		display: grid;
		gap: clamp(2.5rem, 4vw, 3.5rem);
		margin-top: clamp(3rem, 5vw, 4rem);
		align-items: start;
	}
	@media (min-width: 1000px) {
		.layout {
			grid-template-columns: minmax(0, 7fr) minmax(0, 5fr);
		}
	}

	/* ── The founder card ───────────────────────────────────────────────── */
	.card {
		display: flex;
		flex-direction: column;
		border-radius: var(--radius-win);
		border: 1px solid var(--color-line);
		background-color: var(--color-surface);
		box-shadow: var(--shadow-win);
		overflow: hidden;
	}
	.card__bar {
		display: flex;
		align-items: center;
		gap: 0.75rem;
		padding: 0.65rem 1rem;
		border-bottom: 1px solid var(--color-line);
		background-color: var(--color-panel);
	}
	.card__marks {
		flex: none;
		width: 28px;
		height: 5px;
		background-image: repeating-linear-gradient(
			to right,
			var(--color-ink-300) 0 1px,
			transparent 1px 10px
		);
	}
	.card__cap {
		font-family: var(--font-mono);
		font-size: var(--text-mono-xs);
		letter-spacing: 0.06em;
		text-transform: uppercase;
		color: var(--color-ink-400);
	}
	.card__body {
		padding: clamp(1.75rem, 4vw, 2.5rem);
	}

	/* ── Scarcity ladder — founder presale (current, lit) vs. the price it
	   reverts to once the presale ends (next, muted). Never fakes a "sold out"
	   tier before this one: the founder presale is the first tier, so there is
	   nothing earlier. Markers live in their own full-width row (a flex track
	   independent of the tier text below) so the line always runs marker-to-
	   marker however wide the $900/yr text gets — a shared grid column let
	   content width drag the right marker away from the line's end. ── */
	.ladder__markers {
		position: relative;
		display: flex;
		align-items: center;
		justify-content: space-between;
	}
	.ladder__markers::before {
		content: '';
		position: absolute;
		left: 6px;
		right: 6px;
		top: 50%;
		height: 1px;
		background-color: var(--color-line-strong);
		transform: translateY(-50%);
	}
	.ladder__marker {
		position: relative;
		z-index: 1;
		width: 12px;
		height: 12px;
		border: 1px solid var(--color-line-strong);
		background-color: var(--color-panel);
	}
	.ladder__marker.is-active {
		border-color: var(--color-cut-600);
		background-color: var(--color-cut-400);
		box-shadow: 0 0 0 3px color-mix(in srgb, var(--color-cut-400) 30%, transparent);
	}
	.ladder__tiers {
		display: flex;
		align-items: flex-start;
		justify-content: space-between;
		gap: 1rem;
		margin-top: 0.85rem;
	}
	.ladder__price {
		font-family: var(--font-display);
		font-size: clamp(2.75rem, 6vw, 4rem);
		font-weight: 600;
		line-height: 1;
		letter-spacing: -0.035em;
		color: var(--color-ink-900);
	}
	.ladder__price--spent {
		color: var(--color-ink-300);
	}
	.ladder__suffix {
		margin-top: 0.375rem;
		color: var(--color-ink-400);
	}
	.ladder__next {
		text-align: right;
	}
	.ladder__next-price {
		font-family: var(--font-display);
		font-size: var(--text-h3);
		font-weight: 600;
		/* ink-400: a muted anchor price, but still text — it must hold AA. */
		color: var(--color-ink-400);
		text-decoration: line-through;
		text-decoration-color: var(--color-ink-300);
	}
	.ladder__next-suffix {
		font-size: 0.875rem;
		font-weight: 400;
	}
	.ladder__label {
		margin-top: 0.5rem;
		font-family: var(--font-mono);
		font-size: var(--text-mono-s);
		letter-spacing: 0.04em;
		text-transform: uppercase;
		color: var(--color-ink-400);
	}
	.ladder__label--accent {
		color: var(--color-cut-700);
	}
	.ladder__count {
		font-family: var(--font-display);
		font-size: 1.1em;
		font-weight: 600;
	}

	/* ── Bullets ────────────────────────────────────────────────────────── */
	.bullets {
		display: flex;
		flex-direction: column;
		gap: 0.75rem;
		margin-top: 2rem;
	}
	.bullets li {
		position: relative;
		padding-left: 1.375rem;
		color: var(--color-ink-500);
	}
	.bullets li::before {
		content: '';
		position: absolute;
		top: 0.7em;
		left: 0;
		width: 9px;
		height: 1px;
		background-color: var(--color-cut-600);
	}

	.card__who {
		margin-top: 1.5rem;
	}
	.card__prompt {
		margin-top: 2rem;
		max-width: var(--container-measure);
		color: var(--color-ink-500);
	}
	.card__body :global(.card__capture) {
		margin-top: 1.5rem;
	}
	.card__buy {
		margin-top: 2rem;
	}

	.card__terms {
		display: flex;
		flex-direction: column;
		gap: 0.5rem;
		padding: 1.25rem clamp(1.75rem, 4vw, 2.5rem);
		border-top: 1px solid var(--color-line);
		background-color: var(--color-panel);
		color: var(--color-ink-400);
		font-size: 0.9375rem;
	}
	.card__footnote {
		padding: 0.85rem clamp(1.75rem, 4vw, 2.5rem);
		border-top: 1px solid var(--color-line);
		background-color: var(--color-panel);
	}

	/* ── Aside ──────────────────────────────────────────────────────────── */
	.prog {
		margin-top: 1rem;
	}
	.prog__track {
		height: 5px;
		border-radius: 3px;
		background-color: var(--color-line-strong);
		overflow: hidden;
	}
	.prog__fill {
		height: 100%;
		border-radius: 3px;
		background-color: var(--color-cut-600);
	}
	.prog__label {
		margin-top: 0.625rem;
		font-family: var(--font-mono);
		font-size: var(--text-mono-s);
		letter-spacing: 0.04em;
		color: var(--color-ink-400);
	}
	/* The count is live — say so with a pulse, not a claim. Reduced-motion gets
	   the static dot (the global rule parks the keyframes). */
	.prog__live {
		display: inline-block;
		width: 6px;
		height: 6px;
		margin-right: 0.5rem;
		border-radius: 50%;
		background-color: var(--color-cut-600);
		animation: prog-pulse 2.4s var(--ease-out) infinite;
	}
	@keyframes prog-pulse {
		0%,
		100% {
			opacity: 1;
		}
		50% {
			opacity: 0.25;
		}
	}
	.prog__label--wait {
		margin-top: 1rem;
	}
	.prog__n {
		color: var(--color-cut-700);
	}
	.aside__waitlist {
		margin-top: 2.5rem;
		padding-top: 2.5rem;
		border-top: 1px solid var(--color-line);
	}
	.aside__prompt {
		color: var(--color-ink-500);
	}
	.aside :global(.aside__capture) {
		margin-top: 1rem;
	}

	/* ── Notice ─────────────────────────────────────────────────────────── */
	.notice {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 1rem;
		margin-top: 1.5rem;
		border-left: 2px solid var(--color-cut-600);
		padding-block: 0.5rem;
		padding-left: 1rem;
		font-family: var(--font-mono);
		font-size: var(--text-mono-s);
		letter-spacing: 0.04em;
		color: var(--color-ink-700);
	}
	.notice__dismiss {
		display: inline-flex;
		flex: none;
		align-items: center;
		justify-content: center;
		line-height: 1;
		color: var(--color-ink-400);
		cursor: pointer;
		/* Pads the small × glyph out towards a 44px hit target without growing the
		   banner's visible footprint — negative margin pulls it back. */
		padding: 0.75rem;
		margin: -0.75rem -0.25rem -0.75rem -0.75rem;
	}
	.notice__dismiss:hover {
		color: var(--color-ink-900);
	}
</style>
